import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { statsContainerVariants, statsItemVariants } from '@/lib/animations';

type FAQ = {
  id: string;
  question: string;
  answer: string;
};

const FAQS: FAQ[] = [
  {
    id: 'q1',
    question: 'Do I have to buy a domain from Attain Domains to work with you?',
    answer:
      'No. If you already own a strong domain, we can build around that. Attain Domains exists because many people want help picking the right name and launching it properly—but the same launch protocol applies either way.',
  },
  {
    id: 'q2',
    question: 'What if I already have a website?',
    answer:
      'That’s common. We’ll review what’s working, what isn’t, and whether it makes more sense to refine or rebuild. The goal isn’t “a prettier site”—it’s a brand and system that consistently books the right clients.',
  },
  {
    id: 'q3',
    question: 'What kind of clients do you work with?',
    answer:
      'Freelancers, experts, consultants, coaches, and agencies who sell online services—branding, design, development, marketing, legal, accounting, HR, analytics, and more—and are ready to treat their domain like an asset, not a hobby.',
  },
  {
    id: 'q4',
    question: 'How do leasing and lease-to-own work?',
    answer:
      'You pay a set amount each month for a fixed term while you use the domain for your business. When the final payment clears, ownership transfers to you. If you want to pay off early, we’ll help you do that too. Full details are in our support docs.',
  },
];

export function HomeMiniFaq() {
  const reveal = useRevealOnce({ amount: 0.25 });
  const prefersReducedMotion = useReducedMotion();

  return (
    <section id="home-mini-faq" className="bg-slate-50 py-12 md:py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            ref={reveal.ref as any}
            variants={statsContainerVariants}
            initial="hidden"
            animate={reveal.controls}
          >
            <h2 className="text-2xl md:text-3xl font-semibold text-slate-900 mb-6">
              A few quick answers.
            </h2>

            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((faq) => (
                <motion.div key={faq.id} variants={statsItemVariants}>
                  <AccordionItem value={faq.id} className="border-b border-slate-200">
                    <AccordionTrigger className="text-left py-4 text-base font-medium text-slate-900 hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-700 leading-relaxed pt-2 whitespace-pre-line motion-reduce:animate-none motion-reduce:transition-none">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>

            <div className="mt-6">
              <Link
                to="/support"
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                See all FAQs →
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}





